import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Upload, Check, Mail } from "lucide-react";

export const EmailImport = ({ onDone }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/attorneys/import-emails", { csv_text: await file.text() });
      setResult(data);
      toast.success(`Added emails to ${data.updated} listing${data.updated === 1 ? "" : "s"}`);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Import failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <div className="border-2 border-black/10 bg-secondary/40 p-4 mb-6" data-testid="email-import">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <p className="text-sm font-semibold">Import lawyer emails (for invites)</p>
          <p className="text-xs text-muted-foreground">Upload a CSV with an <b>email</b> column plus <b>bar_id</b> or <b>name</b>. Emails are private — used only for invites, never shown publicly.</p>
        </div>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="email-import-file" />
        <Button onClick={() => fileRef.current?.click()} disabled={busy} variant="outline" className="rounded-sm gap-2 shrink-0" data-testid="email-import-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload CSV
        </Button>
      </div>
      {result && (
        <p className="text-xs text-muted-foreground mt-3" data-testid="email-import-result">
          {result.updated} updated · {result.unmatched_count} not matched{result.invalid.length ? ` · ${result.invalid.length} invalid emails` : ""}
          {result.unmatched.length > 0 && <span className="block mt-1 truncate">Not matched: {result.unmatched.slice(0, 12).join(", ")}{result.unmatched_count > 12 ? "…" : ""}</span>}
        </p>
      )}
    </div>
  );
};

export const ContactEmailInline = ({ a, onSaved }) => {
  const [val, setVal] = useState(a.contact_email || "");
  const [editing, setEditing] = useState(false);
  const save = async () => {
    try {
      const { data } = await api.patch(`/admin/attorneys/${a.id}/contact-email`, { email: val.trim() || null });
      toast.success(data.contact_email ? "Invite email saved" : "Invite email removed");
      setEditing(false); onSaved?.(data.contact_email);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Enter a valid email"); }
  };
  if (!editing) return (
    <button type="button" onClick={() => { setVal(a.contact_email || ""); setEditing(true); }} data-testid={`contact-email-${a.id}`}
      className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-accent">
      <Mail size={11} /> {a.contact_email ? `Invite email: ${a.contact_email}` : "Add invite email"}
    </button>
  );
  return (
    <div className="mt-1 flex items-center gap-2">
      <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder="lawyer@firm.com" className="h-8 w-60 rounded-sm text-xs" data-testid={`contact-email-input-${a.id}`}
        onKeyDown={(e) => e.key === "Enter" && save()} autoFocus />
      <Button size="sm" onClick={save} className="h-8 rounded-sm" data-testid={`contact-email-save-${a.id}`}><Check size={13} /></Button>
      <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-8">Cancel</Button>
    </div>
  );
};
